import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const prevPendingRef = useRef(0);
  const notifiedTxids = useRef(new Set());

  // Pix settings
  const [pixSettings, setPixSettings] = useState(null);
  const [pixEdit, setPixEdit] = useState(false);
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('email');
  const [pixMerchantName, setPixMerchantName] = useState('');
  const [pixMerchantCity, setPixMerchantCity] = useState('');
  const [pixSaving, setPixSaving] = useState(false);
  const [pixMsg, setPixMsg] = useState(null);

  // Pending payments
  const [pendPayments, setPendPayments] = useState([]);
  const [unassignedPayments, setUnassignedPayments] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [selectedDestinations, setSelectedDestinations] = useState({});
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  const [pendPaymentsLoading, setPendPaymentsLoading] = useState(false);

  const fetchPixSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/payments/admin/pix-settings');
      if (data) {
        setPixSettings(data);
        setPixKey(data.pix_key || '');
        setPixKeyType(data.pix_key_type || 'email');
        setPixMerchantName(data.merchant_name || '');
        setPixMerchantCity(data.merchant_city || '');
      }
    } catch (_) { /* não configurado ainda */ }
  }, []);

  const fetchUnassignedPayments = useCallback(async () => {
    setUnassignedLoading(true);
    try {
      const [{ data: unassigned }, { data: businesses }] = await Promise.all([
        api.get('/payments/admin/payments/unassigned'),
        api.get('/admin/businesses', { params: { status: 'approved', limit: 50 } }),
      ]);
      setUnassignedPayments(unassigned.data || []);
      setApprovedBusinesses(businesses.data || []);
    } catch (_) {
      setUnassignedPayments([]);
      setApprovedBusinesses([]);
    } finally {
      setUnassignedLoading(false);
    }
  }, []);

  const handleAssignPayment = async (payment) => {
    const businessId = selectedDestinations[payment.id];
    if (!businessId) return;
    try {
      await api.post(`/payments/admin/payments/${payment.id}/assign`, { business_id: Number(businessId) });
      setPixMsg({ type: 'ok', text: payment.status === 'pending_payment' ? 'Destino definido. Agora confirme o pagamento.' : 'Créditos reconciliados no estabelecimento.' });
      setSelectedDestinations(prev => ({ ...prev, [payment.id]: '' }));
      fetchUnassignedPayments();
      fetchPendingPayments();
    } catch (err) {
      setPixMsg({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao atribuir créditos' });
    }
  };

  const fetchPendingPayments = useCallback(async (silent) => {
    if (!silent) setPendPaymentsLoading(true);
    try {
      const { data } = await api.get('/payments/admin/payments/pending');
      const list = data.data || [];
      const prevLen = pendPayments.length;
      setPendPayments(list);

      // Browser notification: novos pagamentos surgiram desde a última poll
      if (silent && list.length > prevLen && list.length > 0) {
        const newOnes = list.filter(p => !notifiedTxids.current.has(p.pix_txid));
        if (newOnes.length > 0) {
          newOnes.forEach(p => notifiedTxids.current.add(p.pix_txid));
          if (Notification.permission === 'granted') {
            const body = newOnes.length === 1
              ? `${newOnes[0].user_name} comprou ${newOnes[0].amount} créditos`
              : `${newOnes.length} novos pagamentos pendentes`;
            new Notification('💰 Pagamento Pix pendente', {
              body,
              icon: '/favicon.ico',
              tag: 'pix-pending',
            });
          } else if (Notification.permission === 'default' && list.length >= 2) {
            // Pede permissão após a 2ª pendência (evita spam no primeiro acesso)
            Notification.requestPermission();
          }
        }
      }
    } catch (_) {
      if (!silent) setPendPayments([]);
    } finally {
      if (!silent) setPendPaymentsLoading(false);
    }
  }, [pendPayments.length]);

  useEffect(() => {
    (async () => {
      try {
        const [dashRes, pendingRes] = await Promise.all([
          api.get('/admin/dashboard'),
          api.get('/admin/businesses/pending'),
        ]);
        setStats(dashRes.data);
        setPending(Array.isArray(pendingRes.data) ? pendingRes.data.length : (pendingRes.data.data || []).length);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
    fetchPixSettings();
    fetchPendingPayments(false);
    fetchUnassignedPayments();

    // Polling silencioso a cada 30s (pagamentos pendentes + compras sem destino)
    const poll = setInterval(() => { fetchPendingPayments(true); fetchUnassignedPayments(); }, 30000);
    return () => clearInterval(poll);
  }, [fetchPixSettings, fetchPendingPayments, fetchUnassignedPayments]);

  const handleSavePix = async () => {
    setPixSaving(true);
    setPixMsg(null);
    try {
      const { data } = await api.put('/payments/admin/pix-settings', {
        pix_key: pixKey,
        pix_key_type: pixKeyType,
        merchant_name: pixMerchantName,
        merchant_city: pixMerchantCity,
      });
      setPixSettings(data);
      setPixEdit(false);
      setPixMsg({ type: 'ok', text: 'Chave Pix atualizada com sucesso!' });
    } catch (err) {
      setPixMsg({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao salvar' });
    } finally {
      setPixSaving(false);
    }
  };

  const handleConfirmPayment = async (id) => {
    try {
      await api.post(`/payments/admin/payments/${id}/confirm`);
      setPixMsg({ type: 'ok', text: 'Pagamento confirmado! Créditos liberados.' });
      fetchPendingPayments();
    } catch (err) {
      setPixMsg({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao confirmar' });
    }
  };

  const handleCancelPayment = async (id) => {
    try {
      await api.post(`/payments/admin/payments/${id}/cancel`);
      setPixMsg({ type: 'ok', text: 'Pagamento cancelado.' });
      fetchPendingPayments();
    } catch (err) {
      setPixMsg({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao cancelar' });
    }
  };

  const cards = stats ? [
    { label: 'Cidades', value: stats.cities, icon: '🏙️', color: 'var(--nc-primary)' },
    { label: 'Comércios', value: stats.businesses, icon: '🏪', color: 'var(--nc-primary)' },
    { label: 'Usuários', value: stats.users, icon: '👥', color: 'var(--nc-primary)' },
    { label: 'Pendentes de aprovação', value: pending, icon: '⏳', color: 'var(--nc-warning-text)' },
    ...(pendPayments.length > 0 ? [{ label: 'Pagamentos Pix pendentes', value: pendPayments.length, icon: '💰', color: 'var(--nc-boost)' }] : []),
  ] : [];

  const tools = [
    { path: '/admin/comercios', label: 'Comércios', desc: 'Aprovar, suspender e gerenciar', icon: '🏪' },
    { path: '/admin/cidades', label: 'Cidades', desc: 'Cadastrar e ativar cidades', icon: '🏙️' },
    { path: '/admin/moderacao', label: 'Moderação', desc: 'Publicações do feed', icon: '📢' },
    { path: '/admin/relatorio', label: 'Relatório de vendas', desc: 'Créditos vendidos por período', icon: '📊' },
    { path: '/admin/convites', label: 'Convites de cadastro', desc: 'Gerar código de acesso e enviar pelo WhatsApp', icon: '🔑' },
    { path: '/admin/importacoes', label: 'Importações', desc: 'Homologar registros do coletor e decidir duplicidades', icon: '📦' },
    { path: '/admin/reivindicacoes', label: 'Reivindicações', desc: 'Analisar quem é dono de cada comércio', icon: '🏪' },
    { path: '/comerciante/impulsionar', label: 'Impulsionar estabelecimento', desc: 'Selecionar comércio e ativar destaque no mapa', icon: '⚡' },
  ];

  return (
    <div className="nc-page" style={{ paddingBottom: 40 }}>
      <div className="nc-pagehead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navigate('/perfil')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            Painel OthoSystems
            {pendPayments.length > 0 && (
              <span style={{
                background: 'var(--nc-danger)', color: 'var(--nc-on-primary)', fontSize: 12, fontWeight: 700,
                minWidth: 20, height: 20, borderRadius: 10, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                animation: 'nc-pulse-badge 2s infinite',
              }}>
                {pendPayments.length}
              </span>
            )}
          </h1>
        </div>
        <p style={{ fontSize: 13, opacity: 0.85, margin: 0 }}>Administração da plataforma Nossa Cidade</p>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading /> : !stats ? (
          <div role="alert" style={{ padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 13 }}>
            Não foi possível carregar o painel.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {cards.map(card => (
                <div key={card.label} style={{
                  background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)',
                  padding: 14, display: 'flex', alignItems: 'center', gap: 12
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10, background: 'var(--nc-success-soft)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                  }}>{card.icon}</div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
                    <div style={{ fontSize: 11, color: 'var(--nc-text-secondary)' }}>{card.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>
              Ferramentas
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tools.map(tool => (
                <button
                  key={tool.path}
                  onClick={() => navigate(tool.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                    background: 'var(--nc-surface)', border: '1px solid var(--nc-border)', borderRadius: 12,
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: 24 }}>{tool.icon}</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{tool.label}</span>
                    <span className="nc-hint" style={{ display: 'block' }}>{tool.desc}</span>
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              ))}
            </div>

            {/* ───────── Pix Settings ───────── */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>
              💳 Configuração Pix
            </div>
            {pixMsg && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 500,
                background: pixMsg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${pixMsg.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
                color: pixMsg.type === 'ok' ? '#15803d' : '#b91c1c',
              }}>
                {pixMsg.text}
              </div>
            )}
            <div style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 16 }}>
              {!pixEdit ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)' }}>
                      <span style={{ marginRight: 8 }}>🔑</span>
                      {pixSettings ? 'Chave Pix configurada' : 'Nenhuma chave Pix configurada'}
                    </div>
                    <button onClick={() => setPixEdit(true)} style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid var(--nc-border)',
                      background: 'var(--nc-surface)', color: 'var(--nc-boost)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                    }}>
                      {pixSettings ? 'Editar' : 'Configurar'}
                    </button>
                  </div>
                  {pixSettings && (
                    <div className="nc-body-sm" style={{ lineHeight: 1.8 }}>
                      <div>Chave: <strong style={{ color: 'var(--nc-text)' }}>{pixSettings.pix_key}</strong></div>
                      <div>Tipo: <strong style={{ color: 'var(--nc-text)' }}>{pixSettings.pix_key_type}</strong></div>
                      <div>Nome: {pixSettings.merchant_name} • Cidade: {pixSettings.merchant_city}</div>
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        {pixSettings.is_active ? '🟢 Ativo' : '🔴 Inativo'}
                      </div>
                    </div>
                  )}
                  {!pixSettings && (
                    <div style={{ fontSize: 13, color: 'var(--nc-text-muted)' }}>
                      Configure sua chave Pix para receber pagamentos de créditos.
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)', display: 'block', marginBottom: 4 }}>
                    Chave Pix *
                  </label>
                  <input
                    value={pixKey} onChange={e => setPixKey(e.target.value)}
                    placeholder="admin@nossacidade.com.br"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', marginBottom: 10 }}
                  />
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)', display: 'block', marginBottom: 4 }}>
                    Tipo de chave
                  </label>
                  <select value={pixKeyType} onChange={e => setPixKeyType(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 14, background: 'var(--nc-surface)', boxSizing: 'border-box', marginBottom: 10 }}
                  >
                    <option value="email">E-mail</option>
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                    <option value="phone">Telefone</option>
                    <option value="random">Chave Aleatória</option>
                  </select>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)', display: 'block', marginBottom: 4 }}>
                    Nome do recebedor (max 25)
                  </label>
                  <input value={pixMerchantName} onChange={e => setPixMerchantName(e.target.value)}
                    placeholder="Nossa Cidade" maxLength={25}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', marginBottom: 10 }}
                  />
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)', display: 'block', marginBottom: 4 }}>
                    Cidade do recebedor (max 15)
                  </label>
                  <input value={pixMerchantCity} onChange={e => setPixMerchantCity(e.target.value)}
                    placeholder="SAO PAULO" maxLength={15}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', marginBottom: 14 }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSavePix} disabled={pixSaving || !pixKey.trim()}
                      style={{
                        flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                        background: pixKey.trim() ? 'linear-gradient(135deg,#7c3aed,#5b21b6)' : '#d1d5db',
                        color: 'var(--nc-on-primary)', fontSize: 14, fontWeight: 600, cursor: pixKey.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {pixSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button onClick={() => { setPixEdit(false); setPixMsg(null); }}
                      style={{
                        flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--nc-border)',
                        background: 'var(--nc-surface)', color: 'var(--nc-text-secondary)', fontSize: 14, cursor: 'pointer'
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ───────── Unassigned Payments ───────── */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>
              🧾 Compras sem estabelecimento ({unassignedPayments.length})
            </div>
            {unassignedLoading ? <Loading /> : unassignedPayments.length === 0 ? (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--nc-text-muted)' }}>
                Nenhuma compra pendente de reconciliação.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {unassignedPayments.map(p => (
                  <div key={p.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-warning-border)', padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>Compra #{p.id} — {p.amount} créditos</div>
                    <div className="nc-hint" style={{ marginTop: 3 }}>{p.user_name} • {p.status === 'pending_payment' ? 'pagamento pendente' : 'pagamento confirmado'}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <select
                        aria-label={`Destino da compra ${p.id}`}
                        value={selectedDestinations[p.id] || ''}
                        onChange={e => setSelectedDestinations(prev => ({ ...prev, [p.id]: e.target.value }))}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: 'var(--nc-surface)', fontSize: 12 }}
                      >
                        <option value="">Selecione o estabelecimento</option>
                        {approvedBusinesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                      <button onClick={() => handleAssignPayment(p)} disabled={!selectedDestinations[p.id]} className="nc-btn-primary" style={{ padding: '8px 12px', fontSize: 12 }}>
                        Atribuir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ───────── Pending Payments ───────── */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>
              💰 Pagamentos Pix pendentes ({pendPayments.length})
            </div>
            {pendPaymentsLoading ? <Loading /> : pendPayments.length === 0 ? (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--nc-text-muted)' }}>
                Nenhum pagamento pendente.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pendPayments.map(p => (
                  <div key={p.id} style={{
                    background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{p.user_name}</div>
                        <div className="nc-hint">{p.user_email}</div>
                        {p.business_name && <div style={{ fontSize: 12, color: 'var(--nc-boost)', marginTop: 2 }}>🏪 {p.business_name}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nc-boost)' }}>{p.amount} créditos</div>
                        <div className="nc-caption">ID: {p.id}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#92400e', background: 'var(--nc-warning-soft)', padding: '6px 10px', borderRadius: 8, marginBottom: 8 }}>
                      TXID: <code style={{ fontSize: 11 }}>{p.pix_txid}</code>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleConfirmPayment(p.id)}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                          background: 'linear-gradient(135deg,var(--nc-success-text),#166534)', color: 'var(--nc-on-primary)',
                          fontSize: 12, fontWeight: 600, cursor: 'pointer'
                        }}
                      >
                        ✅ Confirmar pagamento
                      </button>
                      <button onClick={() => handleCancelPayment(p.id)}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8,
                          border: '1px solid var(--nc-danger-border)', background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)',
                          fontSize: 12, fontWeight: 600, cursor: 'pointer'
                        }}
                      >
                        ❌ Cancelar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
